import { useMemo } from 'react'
import _ from 'lodash'
import { displayReadableTime } from '../../services/displayReadableTime'
import { CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import MyTable from '../MyTable/MyTable'
import { object, string, func, number, shape, bool } from 'prop-types'

const styles = makeStyles((theme) => ({
  centerDiv: {
    margin: 'auto',
    padding: '10px',
    textAlign: 'center',
  },
  tableData: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  spinner: {
    color: theme.palette.primary.main,
  },
}))

// This is a custom aaggregator that takes in an array of leaf values and returns a string
function pivotAggregator(leafValues) {
  let values = leafValues[0] || ''

  leafValues.forEach((value) => {
    if (String(value) !== '') {
      if (values !== '') {
        values = String(values) + ', ' + String(value)
      } else {
        values = String(value)
      }
    }
  })
  return values
}

const CampaignSummaryItem = (props) => {
  const classes = styles(props)
  const {
    timezoneDataFromParent,
    reportDetailsDataFromParent,
    fetchingState,
    pageFromParent,
    pageSizeFromParent,
    reportDetailsSummaryFromParent,
    callbackFromParentPage,
    callbackFromParentPageSize,
    callbackFromParentResponse,
    callbackFromParentContactData,
    blossomIDFromParent,
    jobIDFromParent,
  } = props

  var tempData = []

  const columns = useMemo(
    () => [
      {
        Header: 'Campaign Details',
        columns: [
          {
            Header: 'TM Number',
            accessor: 'contact_id',
          },
          {
            Header: 'First Name',
            accessor: 'first_name',
            aggregate: (firstName, rows) => _.last(firstName),
          },
          {
            Header: 'Last Name',
            accessor: 'last_name',
            aggregate: (lastName, rows) => _.last(lastName),
          },
          {
            Header: 'Location',
            accessor: 'store_number',
            maxWidth: 60,
            aggregate: (storeNumber, rows) => _.last(storeNumber),
          },
          {
            Header: 'Loc Type',
            accessor: 'location_type',
            maxWidth: 60,
            aggregate: (location_type, rows) => _.last(location_type),
          },
          {
            Header: 'Country',
            accessor: 'country_code',
            maxWidth: 60,
            aggregate: (countryCode, rows) => _.last(countryCode),
          },
          {
            Header: 'Contact Type',
            accessor: 'contact_type',
            aggregate: pivotAggregator,
            Cell: (row) => <div className={classes.tableData}>{row.value}</div>,
          },
          {
            Header: 'Contact Phone / Email',
            accessor: 'contact',
            aggregate: pivotAggregator,
            Cell: (row) => <div className={classes.tableData}>{row.value}</div>,
          },
          {
            Header: 'Contact Attempt Time',
            accessor: 'contact_attempt_time',
            aggregate: pivotAggregator,
            Cell: (row) => <div className={classes.tableData}>{row.value}</div>,
          },
          {
            Header: 'Last Disposition',
            accessor: 'last_nw_disposition_time',
            aggregate: pivotAggregator,
            Cell: (row) => <div className={classes.tableData}>{row.value}</div>,
          },
          {
            Header: 'Detailed Desc',
            accessor: 'contact_desc',
            aggregate: pivotAggregator,
            Cell: (row) => <div className={classes.tableData}>{row.value}</div>,
          },
        ],
      },
    ],
    [], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const scrubData = (jsonList) => {
    if (jsonList == null) {
      jsonList = []
    }
    var newData1 = jsonList.map((el) => {
      if (el.contact_attempt_time !== 'None') {
        return Object.assign({}, el, {
          contact_attempt_time: displayReadableTime(
            el.contact_attempt_time,
            timezoneDataFromParent,
          ),
        })
      }
      return el
    })
    var newData2 = newData1.map((el) => {
      if (el.last_nw_disposition_time !== '') {
        return Object.assign({}, el, {
          last_nw_disposition_time: displayReadableTime(
            el.last_nw_disposition_time,
            timezoneDataFromParent,
          ),
        })
      }
      return el
    })
    var newData4 = newData2.map((el) => {
      if (el.contact_desc === 'SMS Queued') {
        return Object.assign({}, el, { contact_desc: 'SMS Sent' })
      }
      return el
    })
    var newData5 = newData4.map((el) => {
      if (el.result === 'Positive' && el.contact_type === 'Phone') {
        return Object.assign({}, el, { contact_desc: 'Safe-Phone' })
      }
      return el
    })
    var newData6 = newData5.map((el) => {
      if (el.result === 'Negative' && el.contact_type === 'Phone') {
        return Object.assign({}, el, { contact_desc: 'Follow-up-Phone' })
      }
      return el
    })
    var newData7 = newData6.map((el) => {
      if (el.result === 'Positive' && el.contact_type === 'SMS') {
        return Object.assign({}, el, { contact_desc: 'Safe-SMS' })
      }
      return el
    })
    var newData8 = newData7.map((el) => {
      if (el.result === 'Negative' && el.contact_type === 'SMS') {
        return Object.assign({}, el, { contact_desc: 'Follow-up-SMS' })
      }
      return el
    })
    var newData9 = newData8.map((el) => {
      if (el.result === 'Positive' && el.contact_type === 'Email') {
        return Object.assign({}, el, { contact_desc: 'Safe-Email' })
      }
      return el
    })
    var newData10 = newData9.map((el) => {
      if (el.result === 'Negative' && el.contact_type === 'Email') {
        return Object.assign({}, el, { contact_desc: 'Follow-up-Email' })
      }
      return el
    })
    return newData10
  }

  tempData = scrubData(reportDetailsDataFromParent['data'])

  if (fetchingState) {
    return (
      <div className={classes.centerDiv}>
        <CircularProgress className={classes.spinner} thickness={7} />
        <h3>loading...</h3>
      </div>
    )
  } else {
    return (
      <MyTable
        columns={columns}
        data={tempData}
        defaultPageIndex={pageFromParent}
        defaultPageSize={pageSizeFromParent}
        disableSort={true}
        maxPageCount={Math.ceil(
          reportDetailsSummaryFromParent['reporting_summary'][
            'total_contacts'
          ] / pageSizeFromParent,
        )}
        defaultSortColumn={[
          {
            id: 'contact_id',
            desc: false,
          },
        ]}
        groupByID={['contact_id']}
        manualPagination={true}
        onPageChange={(pageIndex) => {
          callbackFromParentPage(pageIndex)
          callbackFromParentContactData(
            blossomIDFromParent,
            jobIDFromParent,
            pageIndex,
            pageSizeFromParent,
          )
        }}
        onPageSizeChange={(pageSize, pageIndex) => {
          callbackFromParentPage(pageIndex)
          callbackFromParentPageSize(pageSize)
          callbackFromParentContactData(
            blossomIDFromParent,
            jobIDFromParent,
            pageIndex,
            pageSize,
          )
        }}
        onResponseChange={(pageSize, pageIndex, response) => {
          callbackFromParentPage(pageIndex)
          callbackFromParentPageSize(pageSize)
          callbackFromParentResponse(
            blossomIDFromParent,
            jobIDFromParent,
            pageIndex,
            pageSize,
            response,
          )
        }}
      />
    )
  }
}

export default CampaignSummaryItem

CampaignSummaryItem.propTypes = {
  timezoneDataFromParent: string.isRequired,
  reportDetailsDataFromParent: shape({
    data: object,
    total_contacts: number,
    unique_contacts: shape({
      sms_counter: number,
      email_counter: number,
      phone_counter: number,
    }),
  }),
  fetchingState: bool,
  pageFromParent: number,
  pageSizeFromParent: number,
  reportDetailsSummaryFromParent: shape({
    campaign_summary: object,
    reporting_summary: object,
    unique_response_summary: object,
  }),
  callbackFromParentPage: func.isRequired,
  callbackFromParentPageSize: func.isRequired,
  callbackFromParentContactData: func.isRequired,
  callbackFromParentResponse: func.isRequired,
  blossomIDFromParent: string.isRequired,
  jobIDFromParent: number.isRequired,
}
